import { Alert, Box, Chip, Divider, Typography } from "@mui/material";
import type {
  PaymentDeduction,
  PaymentExecution,
  PaymentOwnerDetail,
  PaymentRequest,
} from "../types/payment";
import type { V2InvoiceDetailResponse } from "../../InvoiceManagement/v2/types/invoice";
import type { PODetailsItem } from "../../InvoiceManagement/v2/api/invoiceQueries";
import PaymentTypeBadge from "./PaymentTypeBadge";
import PaymentStatusBadge from "./PaymentStatusBadge";
import { formatAmount, formatDate } from "../utils/formatters";
import { PAYMENT_STATUS } from "../types/common";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Label + value row — single column, matches v2 FormField pattern. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box display="flex" alignItems="center" gap={3} py={1}>
      <Typography variant="subtitle1" sx={{ width: 140, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );
}

/** Inline flag tag — consistent with PaymentStatusBadge style. */
function FlagTag({ label, color, bgColor }: { label: string; color: string; bgColor: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: "8px",
        py: "4px",
        borderRadius: "4px",
        color,
        backgroundColor: bgColor,
      }}
    >
      <Typography
        component="span"
        sx={{ color: "inherit", fontSize: "10px", fontWeight: 500, lineHeight: "12px", textTransform: "uppercase" }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/** Bordered item card for GRN / Prior Payment rows — matches prototype bordered-box pattern. */
function ItemCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: "6px",
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {children}
    </Box>
  );
}

/** "Less PO Advance" / "Less Open Advance"; an unknown source still reads sensibly. */
export function deductionLabel(source: string): string {
  if (source === "ADVANCE") return "Less PO Advance";
  if (source === "OPEN_ADVANCE") return "Less Open Advance";
  const words = source
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
  return words ? `Less ${words}` : "Less Deduction";
}

/**
 * Why the request is smaller than the invoice it settles: starting amount →
 * each deduction → requested, in the order the backend netted them off. A
 * line names the document behind it (the credit-note number for open advance)
 * and omits it when absent. The PO advance line names nothing: a PO's advance
 * only nets off invoices on that same PO, so its PO code would just repeat
 * the PO Code row above.
 */
function DeductionBreakdown({
  grossLabel,
  grossAmount,
  deductions,
  requestedAmount,
}: {
  grossLabel: string;
  grossAmount: number;
  deductions: PaymentDeduction[];
  requestedAmount: number;
}) {
  return (
    <Box data-testid="deduction-breakdown">
      <Divider sx={{ my: 1 }} />
      <Row label={grossLabel}>
        <Typography variant="body1">{formatAmount(grossAmount)}</Typography>
      </Row>
      {deductions.map((d, i) => {
        const reference = d.source === "ADVANCE" ? null : d.credit_note_number || d.po_code;
        return (
          <Row key={`${d.source}-${d.entry_id ?? i}`} label={deductionLabel(d.source)}>
            <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
              <Typography variant="body1" sx={{ color: "#DB382A" }}>
                −{formatAmount(d.amount)}
              </Typography>
              {reference && (
                <Typography variant="body2" color="text.secondary">
                  {reference}
                </Typography>
              )}
            </Box>
          </Row>
        );
      })}
      <Divider sx={{ my: 0.5 }} />
      <Row label="Requested">
        <Typography variant="body1" sx={{ fontWeight: 700, color: "primary.main" }}>
          {formatAmount(requestedAmount)}
        </Typography>
      </Row>
    </Box>
  );
}

/** Section label for list sections (Prior Payments, GRN Details). */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="h4" mb={1.5}>
      {children}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// PaymentDetailContent
// ---------------------------------------------------------------------------

export interface PaymentDetailContentProps {
  request: PaymentRequest;
  executions: PaymentExecution[];
  /** Present only for INVOICE-type requests once useInvoiceDetail resolves. */
  invoiceDetail?: V2InvoiceDetailResponse;
  /** Present when invoiceDetail provides a po_code and usePODetails resolves. */
  poDetails?: PODetailsItem;
  /** Owner entity from GET /payment/:id — provides PO details for PO-type requests directly. */
  owner?: PaymentOwnerDetail | null;
  /** Amounts netted off before the request was raised (GET /payment/:id, ENG-27317). */
  deductions?: PaymentDeduction[] | null;
  /** The request before deductions; derived from the deductions when absent. */
  grossAmount?: number | null;
}

export default function PaymentDetailContent({
  request,
  executions,
  invoiceDetail,
  poDetails,
  owner,
  deductions,
  grossAmount,
}: PaymentDetailContentProps) {
  const invoice = invoiceDetail?.invoice;
  const grnMappings = invoiceDetail?.grn_mappings ?? [];
  // An empty list (a PO-owned ADVANCE request, or a backend hiccup) shows the
  // plain Requested row — never an empty breakdown.
  const netted = deductions ?? [];
  const hasBreakdown = netted.length > 0;
  const gross =
    grossAmount ?? request.requested_amount + netted.reduce((sum, d) => sum + d.amount, 0);
  // The breakdown starts from the invoice value — the backend's gross is the
  // invoice less nothing yet, so the two match. Compared with a paisa of slack
  // because the gross is summed server-side. If they ever differ, the invoice
  // value stays as its own row and the breakdown starts from "Gross Amount",
  // so the lines still add up.
  const invoiceValue = invoice?.invoice_value;
  const grossIsInvoiceValue = invoiceValue != null && Math.abs(invoiceValue - gross) < 0.005;
  const shownInvoiceValue =
    invoiceValue != null && !(hasBreakdown && grossIsInvoiceValue) ? invoiceValue : null;

  return (
    <Box display="flex" flexDirection="column">
      {/* Status & flag tags */}
      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <PaymentStatusBadge status={request.request_status} />
        {request.overdue_flag && (
          <FlagTag label={`${request.days_overdue}d overdue`} color="#DB382A" bgColor="#FBEBEA" />
        )}
        {request.msme_breach_risk_flag && (
          <FlagTag label="MSME breach risk" color="#AF2D22" bgColor="#FBEBEA" />
        )}
        {request.escalation_flag && (
          <FlagTag label="Escalated" color="#DB382A" bgColor="#FBEBEA" />
        )}
        {invoice?.original_invoice_id != null && (
          <FlagTag label="Revised" color="#F19E2B" bgColor="#FFF8E1" />
        )}
      </Box>

      {/* Review blockers — only when the backend explicitly sends the field (post-vip#288) */}
      {request.request_status === PAYMENT_STATUS.UNDER_REVIEW && Array.isArray(request.review_blockers) && (
        <Alert severity="warning" sx={{ mb: 2, alignItems: "flex-start" }}>
          {request.review_blockers.length > 0 ? (
            request.review_blockers.map((b, i) => (
              <Typography key={b.code || i} variant="body2">{b.message || b.code}</Typography>
            ))
          ) : (
            <Typography variant="body2">
              No automated blocker detected. This request requires manual review.
            </Typography>
          )}
        </Alert>
      )}

      {/* Identity / context rows */}
      <Row label={request.owner_type === "INVOICE" ? "Invoice" : "PO"}>
        <Typography variant="body1">
          {invoice?.invoice_no ??
            (request.owner_type === "PO" ? owner?.po_code : owner?.invoice_no) ??
            request.owner_reference}
        </Typography>
      </Row>

      {(invoiceDetail?.vendor_name || (request.owner_type === "PO" && (owner?.vendor_facility_name || request.vendor_facility_name))) && (
        <Row label="Vendor">
          <Typography variant="body1">{invoiceDetail?.vendor_name || owner?.vendor_facility_name || request.vendor_facility_name}</Typography>
        </Row>
      )}

      {invoice?.po_code && (
        <Row label="PO Code">
          <Typography variant="body1">{invoice.po_code}</Typography>
        </Row>
      )}

      {poDetails?.customer_name && (
        <Row label="Customer">
          <Typography variant="body1">{poDetails.customer_name}</Typography>
        </Row>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Financial rows */}
      <Row label="Payment Type">
        <PaymentTypeBadge type={request.payment_type} />
      </Row>

      {(poDetails?.order_value != null || (request.owner_type === "PO" && owner?.order_value != null)) && (
        <Row label="PO Value">
          <Typography variant="body1">
            {formatAmount(poDetails?.order_value ?? owner?.order_value ?? 0)}
          </Typography>
        </Row>
      )}

      {shownInvoiceValue != null && (
        <Row label="Invoice Value">
          <Typography variant="body1">{formatAmount(shownInvoiceValue)}</Typography>
        </Row>
      )}

      {hasBreakdown ? (
        <DeductionBreakdown
          grossLabel={grossIsInvoiceValue ? "Invoice Value" : "Gross Amount"}
          grossAmount={gross}
          deductions={netted}
          requestedAmount={request.requested_amount}
        />
      ) : (
        <Row label="Requested">
          <Typography variant="body1" sx={{ fontWeight: 700, color: "primary.main" }}>
            {formatAmount(request.requested_amount)}
          </Typography>
        </Row>
      )}

      {request.total_paid_amount > 0 && (
        <Row label="Already Paid">
          <Typography variant="body1">{formatAmount(request.total_paid_amount)}</Typography>
        </Row>
      )}

      <Row label="Remaining">
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {formatAmount(request.remaining_amount)}
        </Typography>
      </Row>

      <Row label="Expected Payment">
        <Typography
          variant="body1"
          sx={{ color: request.overdue_flag ? "#DB382A" : "primary.main" }}
        >
          {formatDate(request.expected_payment_date)}
        </Typography>
      </Row>

      {request.msme_deadline_date && (
        <Row label="MSME Deadline">
          <Typography
            variant="body1"
            sx={{ color: request.msme_breach_risk_flag ? "#AF2D22" : "text.primary" }}
          >
            {formatDate(request.msme_deadline_date)}
            {request.msme_breach_risk_flag && " — breach risk"}
          </Typography>
        </Row>
      )}

      {/* Audit rows */}
      <Divider sx={{ my: 1.5 }} />

      <Row label="Source">
        <Typography variant="body1">{request.owner_type}</Typography>
      </Row>

      <Row label="Created By">
        <Typography variant="body1">{request.created_by_name ?? request.created_by}</Typography>
      </Row>

      <Row label="Created">
        <Typography variant="body1">{formatDate(request.created_at)}</Typography>
      </Row>

      {request.approved_by && (
        <Row label="Approved By">
          <Typography variant="body1">{request.approved_by_name ?? request.approved_by}</Typography>
        </Row>
      )}

      {request.advance_approval_date && (
        <Row label="Advance Approved">
          <Typography variant="body1">{formatDate(request.advance_approval_date)}</Typography>
        </Row>
      )}

      {/* GRN Details */}
      {grnMappings.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <SectionLabel>GRN Details</SectionLabel>
          <Box display="flex" flexDirection="column" gap={0.75}>
            {grnMappings.map((grn) => (
              <ItemCard key={grn.id}>
                <Typography sx={{ fontSize: "12px", fontWeight: 700, fontFamily: "monospace" }}>
                  GRN #{grn.grn_id}
                </Typography>
                <Box textAlign="right">
                  <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
                    {formatAmount(grn.allocated_value)}
                  </Typography>
                  {grn.po_code && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {grn.po_code}
                    </Typography>
                  )}
                </Box>
              </ItemCard>
            ))}
          </Box>
        </>
      )}

      {/* Prior Payments */}
      {executions.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <SectionLabel>Prior Payments</SectionLabel>
          <Box display="flex" flexDirection="column" gap={0.75}>
            {executions.map((ex, i) => (
              <ItemCard key={ex.id}>
                <Typography sx={{ fontSize: "13px" }}>Payment #{i + 1}</Typography>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
                    {formatAmount(ex.payment_amount)}
                  </Typography>
                  <Chip label={ex.payment_status} size="small" sx={{ height: 18, fontSize: 10 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                    {formatDate(ex.erp_synced_at ?? ex.created_at)}
                    {ex.utr ? ` • UTR: ${ex.utr}` : ""}
                    {ex.erp_reference_id ? ` • ERP: ${ex.erp_reference_id}` : ""}
                  </Typography>
                </Box>
              </ItemCard>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
